import { PipelineItem, PipelineStatus, PipelineStep, WorkflowStage } from '../../const/PipelineTypes';
import { capitalized } from './PipelineTableFunctionality';

export const PIPELINE_ACTIONS = {
  ReOnboard: 're_onboard',
  ApproveAndPromote: 'approve_and_promote',
  Abort: 'abort',
  Pause: 'pause',
  Retry: 'retry',
  Resume: 'resume',
  Cancel: 'cancel',
  DeploymentCompleted: 'deployment_completed',
  TestingCompleted: 'testing_completed',
  ReviewTestExecutions: 'review_test_executions',
  SelectiveOnboardingApprove: 'selective_onboarding_approve',
  SelectiveOnboardingReject: 'selective_onboarding_reject',
  RappConfigure: 'rapp_configure',
  DeployCrGnb: 'deploy_cr_gnb_nodes',
  DeploymentApprove: 'deployment_approve'
} as const;

export type PipelineActionValue = (typeof PIPELINE_ACTIONS)[keyof typeof PIPELINE_ACTIONS];
export const RE_ONBOARD_ACTION_VALUE = PIPELINE_ACTIONS.ReOnboard;
const RE_ONBOARD_ELIGIBLE_STATUSES: PipelineStatus[] = [
  PipelineStatus.Success,
  PipelineStatus.Failure,
  PipelineStatus.Warning,
  PipelineStatus.Canceled
];

export const deduplicateSteps = (steps: PipelineStep[]): PipelineStep[] => {
  const stepMap = new Map<string, PipelineStep>();
  steps.forEach((step) => {
    const key = step.Step.toLowerCase();
    const existing = stepMap.get(key);
    if (!existing || new Date(step.timestamp).getTime() > new Date(existing.timestamp).getTime()) {
      stepMap.set(key, step);
    }
  });
  return Array.from(stepMap.values());
};

export interface MenuItemsArr {
  label: string;
  value?: PipelineActionValue;
  disabled?: boolean;
  separator?: boolean;
  step?: PipelineStep;
  header?: boolean;
}

export const validateLastStageStatus = (pipeline: PipelineItem): PipelineStep => {
  const steps = pipeline.steps;
  if (!steps.length)
    return { Stage: '', Step: '', Status: PipelineStatus.Pending, timestamp: '', TicketId: '', Environment: [] } as PipelineStep;
  const lastStage = steps.reduce((latest, step) => {
    return new Date(latest.timestamp) > new Date(step.timestamp) ? latest : step;
  });
  return lastStage;
};

const isSelectiveOnboardingArtifactsManual = (pipeline: PipelineItem, lastStage: PipelineStep): boolean => {
  const selectiveOnboardingEnabled = pipeline.automationFlow?.configuration?.selectiveOnboarding?.enabled === true;
  const isArtifactsRetrievingStage = !!lastStage.Stage && lastStage.Stage.toLowerCase().includes('artifacts retrieving');
  const pipelineNotCanceled = pipeline.status?.toLowerCase() !== PipelineStatus.Canceled;

  return selectiveOnboardingEnabled && isArtifactsRetrievingStage && lastStage.Status === PipelineStatus.Manual && pipelineNotCanceled;
};

/** CR gNB with deploymentAutomation OFF at Artifacts Deployment — base condition */
const isCrGnbDeploymentManual = (pipeline: PipelineItem, lastStage: PipelineStep): boolean => {
  // Detect CR gNB / Cloud RAN by ProductLine (the authoritative signal already used elsewhere in
  // this file, e.g. crGnbItem and tooltips). The cardName is "Cloud Ran gNB Workflow", which does
  // NOT contain "cr gnb", so the old cardName check never matched for real flows.
  const isCrGnb = !!pipeline.metadata?.ProductLine?.toLowerCase().includes('cloud ran');
  const deploymentAutomationOff = pipeline.automationFlow?.configuration?.deploymentAutomation === false;
  const isArtifactsDeployment = lastStage.Stage === WorkflowStage.ArtifactsDeployment;
  const isManual = lastStage.Status === PipelineStatus.Manual;
  const notCanceled = pipeline.status?.toLowerCase() !== PipelineStatus.Canceled;

  return isCrGnb && deploymentAutomationOff && isArtifactsDeployment && isManual && notCanceled;
};

/** First pause: Go/No-Go before draft creation */
const isDeploymentApprovalStep = (pipeline: PipelineItem, lastStage: PipelineStep): boolean => {
  return isCrGnbDeploymentManual(pipeline, lastStage) && lastStage.Step === 'Deployment Approval';
};

/** Second pause: node selection / CIQ submission. The ticket is generic (1 ticket upgrades many
 *  nodes, 1 deployment = 1 node), so it carries NO ticket-level deployment_id — per-node
 *  deployment_ids come from the gNB inventory selection at submit time. Do not gate on
 *  pipeline.metadata.deployment_id (it is never set on the ticket). */
const isCiqSubmissionStep = (pipeline: PipelineItem, lastStage: PipelineStep): boolean => {
  return isCrGnbDeploymentManual(pipeline, lastStage) && lastStage.Step === 'CIQ Submission';
};

export const getStageStatus = (stage: string, pipeline: PipelineItem): PipelineStatus => {
  // Get current stage from response
  const currentStage = pipeline.currentStage.toLowerCase();

  // remove when bff fixes the stage name
  if (stage.toLowerCase() === 'artifact onboarding') {
    stage = 'artifacts onboarding';
  }

  // If this is the current stage
  if (stage.toLowerCase() === currentStage) {
    // return pipeline.status; // Use the pipeline's current status
  }

  // If this stage has steps, check for any failures in previous steps
  const filteredStageSteps = pipeline.steps.filter((step) => step.Stage && step.Stage.toLowerCase() === stage.toLowerCase());
  const uniqueSteps = [...new Map(filteredStageSteps?.map((s) => [`${s.Step}-${s.Stage}`, { Step: s.Step, Stage: s.Stage }])).values()];

  const latestSteps = uniqueSteps?.map((stepName) => {
    const filtered = filteredStageSteps.filter((obj) => obj.Step === stepName.Step && obj.Stage === stepName.Stage);

    return filtered?.reduce((latest, obj) => (new Date(obj.timestamp) > new Date(latest.timestamp) ? obj : latest));
  });

  if (latestSteps.length > 0) {
    // Normalize step status for comparison (API may return different casing)
    const stepStatusLower = (s: PipelineStep) => (typeof s.Status === 'string' ? s.Status.toLowerCase() : '');

    // Check for Aborted
    const hasAborted = latestSteps?.some((step) => stepStatusLower(step) === PipelineStatus.Aborted);
    if (hasAborted) {
      return PipelineStatus.Aborted;
    }
    // Check if any previous step has failed
    const hasFailure = latestSteps?.some((step) => stepStatusLower(step) === PipelineStatus.Failure);
    if (hasFailure) {
      return PipelineStatus.Failure;
    }
    // Check for Paused
    const hasPaused = latestSteps?.some((step) => stepStatusLower(step) === PipelineStatus.Paused);
    if (hasPaused) {
      return PipelineStatus.Paused;
    }
    // If no failures, check if any step is in progress, in case some steps comes in again
    const hasInProgress = latestSteps?.some((step) => stepStatusLower(step) === PipelineStatus.InProgress);
    if (hasInProgress) {
      return PipelineStatus.InProgress;
    }
    // Check if any step is canceled (e.g. selective onboarding reject at Artifacts Retrieving)
    const hasCanceled = latestSteps?.some((step) => stepStatusLower(step) === PipelineStatus.Canceled);
    if (hasCanceled) {
      if (stage.toLowerCase().includes('artifacts retrieving')) {
        return PipelineStatus.Warning;
      }
      return PipelineStatus.Canceled;
    }

    const hasManual = latestSteps.filter((obj) => stepStatusLower(obj) === PipelineStatus.Manual);
    if (hasManual.length > 0) {
      // When ticket is canceled at this stage (e.g. reject), show warning even if step still says Manual
      const pipelineStatusLower = (
        pipeline.status && typeof pipeline.status === 'string' ? pipeline.status.toLowerCase() : ''
      ) as PipelineStatus;
      if (pipelineStatusLower === PipelineStatus.Canceled && stage.toLowerCase().includes('artifacts retrieving')) {
        return PipelineStatus.Warning;
      }
      return PipelineStatus.Manual;
    }

    const hasWarning = latestSteps.filter((obj) => stepStatusLower(obj) === PipelineStatus.Warning);
    if (hasWarning.length > 0) {
      return PipelineStatus.Warning;
    }

    // If no failures, in progress, canceled, manual or warning, return the status of the last step (normalized)
    const lastStatus = latestSteps[latestSteps.length - 1].Status;
    const lastLower = typeof lastStatus === 'string' ? lastStatus.toLowerCase() : lastStatus;
    return (lastLower as PipelineStatus) || PipelineStatus.Pending;
  }

  // Default to pending for all other stages
  return PipelineStatus.Pending;
};

export const getMenuItems = (pipeline: PipelineItem): MenuItemsArr[] => {
  const lastStage = validateLastStageStatus(pipeline);
  const pipelineStatus = (pipeline.status || '').toLowerCase() as PipelineStatus;
  const isReOnboardEligible = RE_ONBOARD_ELIGIBLE_STATUSES.includes(pipelineStatus);

  const isCrGnbWithDeployment =
    pipeline.metadata?.ProductLine?.toLowerCase().includes('cloud ran') &&
    !!pipeline.metadata?.deployment_id &&
    pipeline.gnbDeploySubmitted;
  const crGnbItem: MenuItemsArr[] = isCrGnbWithDeployment
    ? [
        { label: 'Node Management', header: true, separator: true },
        { label: 'Check Node Status', value: PIPELINE_ACTIONS.DeployCrGnb, disabled: false, separator: false, step: lastStage }
      ]
    : [];
  const allMenuItems: MenuItemsArr[] = [
    { label: 'Approve & Promote', value: PIPELINE_ACTIONS.ApproveAndPromote, disabled: false, separator: false, step: lastStage },
    { label: 'Abort', value: PIPELINE_ACTIONS.Abort, disabled: false, separator: false, step: lastStage },
    { label: 'Pause', value: PIPELINE_ACTIONS.Pause, disabled: false, separator: false, step: lastStage },
    { label: 'Retry', value: PIPELINE_ACTIONS.Retry, disabled: false, separator: false, step: lastStage },
    { label: 'Resume', value: PIPELINE_ACTIONS.Resume, disabled: false, separator: false, step: lastStage },
    { label: 'Cancel', value: PIPELINE_ACTIONS.Cancel, disabled: false, separator: false, step: lastStage },
    { label: 'Deployment Completed', value: PIPELINE_ACTIONS.DeploymentCompleted, disabled: false, separator: false, step: lastStage },
    { label: 'Testing Completed', value: PIPELINE_ACTIONS.TestingCompleted, disabled: false, separator: false, step: lastStage },
    { label: 'Review Test Executions', value: PIPELINE_ACTIONS.ReviewTestExecutions, disabled: false, separator: true, step: lastStage }
  ];

  if (isReOnboardEligible) {
    allMenuItems.unshift({
      label: 'Re-onboard',
      value: RE_ONBOARD_ACTION_VALUE,
      disabled: false,
      separator: false,
      step: lastStage
    });
  }

  // rApp Pre-Deploy Input: Retry, Cancel + Configure (same pattern as CIQ Upload)
  if (
    lastStage.Stage.toLowerCase().includes('artifacts deployment') &&
    lastStage.Step.toLowerCase().includes('pre-deploy') &&
    lastStage.Status === PipelineStatus.Manual &&
    pipeline.status?.toLowerCase() !== PipelineStatus.Canceled
  ) {
    const items: MenuItemsArr[] = [
      { label: 'Configure', value: PIPELINE_ACTIONS.RappConfigure, disabled: false, separator: false, step: lastStage },
      { label: 'Deployment Completed', value: PIPELINE_ACTIONS.DeploymentCompleted, disabled: false, separator: false, step: lastStage },
      { label: 'Cancel', value: PIPELINE_ACTIONS.Cancel, disabled: false, separator: false, step: lastStage }
    ];
    if (isReOnboardEligible) {
      items.push({ label: 'Re-onboard', value: RE_ONBOARD_ACTION_VALUE, disabled: false, separator: false, step: lastStage });
    }
    return items;
  }

  // Selective Onboarding: Artifacts Retrieving + Manual + selectiveOnboarding.enabled
  if (isSelectiveOnboardingArtifactsManual(pipeline, lastStage)) {
    const selectiveItems: MenuItemsArr[] = [
      {
        label: 'Approve',
        value: PIPELINE_ACTIONS.SelectiveOnboardingApprove,
        disabled: false,
        separator: false,
        step: lastStage
      },
      {
        label: 'Reject',
        value: PIPELINE_ACTIONS.SelectiveOnboardingReject,
        disabled: false,
        separator: false,
        step: lastStage
      }
    ];

    return [...selectiveItems, ...crGnbItem];
  }

  if (pipeline.status === PipelineStatus.Paused) {
    return getMenusWorkflowPaused(allMenuItems);
  }
  if (pipeline.status === PipelineStatus.Aborted || pipeline.status === PipelineStatus.Failure) {
    return getMenusWorkflowAbortedAndFailed(allMenuItems);
  }
  if (lastStage.Stage === WorkflowStage.ArtifactsDeployment && lastStage.Status === PipelineStatus.Manual) {
    return getMenusArtifactsDeploymentManual(pipeline, lastStage, allMenuItems, crGnbItem);
  }
  if (lastStage.Stage === WorkflowStage.Testing && lastStage.Status === PipelineStatus.Warning) {
    return getMenusTestingWarning(allMenuItems, crGnbItem);
  }
  if (lastStage.Stage === WorkflowStage.Testing && lastStage.Status === PipelineStatus.Manual) {
    return getMenusTestingManual(allMenuItems, crGnbItem);
  }

  return getMenusFallback(allMenuItems, crGnbItem, pipeline);
};

const getMenusWorkflowPaused = (allMenuItems: MenuItemsArr[]) => {
  return allMenuItems.filter((mi) => mi.value === PIPELINE_ACTIONS.Resume);
};

const getMenusWorkflowAbortedAndFailed = (allMenuItems: MenuItemsArr[]) => {
  return allMenuItems.filter((mi) => mi.value === PIPELINE_ACTIONS.Retry);
};

const getMenusArtifactsDeploymentManual = (
  pipeline: PipelineItem,
  lastStage: PipelineStep,
  allMenuItems: MenuItemsArr[],
  crGnbItem: MenuItemsArr[]
): MenuItemsArr[] => {
  // CR gNB: first pause — Go/No-Go approval
  if (isDeploymentApprovalStep(pipeline, lastStage)) {
    return [
      { label: 'Approve/Reject Deployment', value: PIPELINE_ACTIONS.DeploymentApprove, disabled: false, separator: false, step: lastStage }
    ];
  }
  // CR gNB: second pause — CIQ upload after draft creation
  if (isCiqSubmissionStep(pipeline, lastStage)) {
    return [
      { label: 'Cancel', value: PIPELINE_ACTIONS.Cancel, disabled: false, separator: false, step: lastStage },
      { label: 'Node Management', header: true, separator: true },
      {
        label: pipeline.gnbDeploySubmitted ? 'Check Node Status' : 'Configure Nodes',
        value: PIPELINE_ACTIONS.DeployCrGnb,
        disabled: false,
        separator: false,
        step: lastStage
      }
    ];
  }
  return [
    ...allMenuItems.filter(
      (mi) =>
        mi.value === PIPELINE_ACTIONS.DeploymentCompleted || mi.value === PIPELINE_ACTIONS.Cancel || mi.value === RE_ONBOARD_ACTION_VALUE
    ),
    ...crGnbItem
  ];
};

const getMenusTestingWarning = (allMenuItems: MenuItemsArr[], crGnbItem: MenuItemsArr[]) => {
  return [
    ...allMenuItems.filter(
      (mi) =>
        mi.value === PIPELINE_ACTIONS.ApproveAndPromote ||
        mi.value === PIPELINE_ACTIONS.Cancel ||
        mi.value === PIPELINE_ACTIONS.ReviewTestExecutions ||
        mi.value === RE_ONBOARD_ACTION_VALUE
    ),
    ...crGnbItem
  ];
};
const getMenusTestingManual = (allMenuItems: MenuItemsArr[], crGnbItem: MenuItemsArr[]) => {
  return [
    ...allMenuItems.filter(
      (mi) => mi.value === PIPELINE_ACTIONS.Cancel || mi.value === PIPELINE_ACTIONS.TestingCompleted || mi.value === RE_ONBOARD_ACTION_VALUE
    ),
    ...crGnbItem
  ];
};

const getMenusFallback = (allMenuItems: MenuItemsArr[], crGnbItem: MenuItemsArr[], pipeline: PipelineItem) => {
  return [
    ...allMenuItems.filter(
      (mi) =>
        mi.value === RE_ONBOARD_ACTION_VALUE ||
        (pipeline.status === PipelineStatus.InProgress &&
          (mi.value === PIPELINE_ACTIONS.Abort || mi.value === PIPELINE_ACTIONS.Pause || mi.value === PIPELINE_ACTIONS.Cancel))
    ),
    ...crGnbItem
  ];
};

export const getStageTooltipTitle = (pipeline: PipelineItem, stageName: string, status: PipelineStatus, isPending: boolean): string => {
  if (
    stageName.toLowerCase().includes('artifacts retrieving') &&
    status === PipelineStatus.Manual &&
    pipeline.automationFlow?.configuration?.selectiveOnboarding?.enabled
  ) {
    return `${stageName}: ${capitalized(status)}. Use Approve or Reject in the Actions menu to continue or cancel the workflow.`;
  }

  if (pipeline?.metadata?.ProductLine?.toLowerCase().includes('cloud ran')) {
    if (stageName === WorkflowStage.Testing && status === PipelineStatus.Warning) {
      return `${stageName}: ${capitalized(status)}. Some Test Cases failed or were skipped. Approve & Promote, Retry or Cancel.`;
    } else if (stageName === WorkflowStage.ArtifactsDeployment && status === PipelineStatus.Manual) {
      if (pipeline.automationFlow?.configuration?.deploymentAutomation === false) {
        return `${stageName}: ${capitalized(status)}. Download CIQ files, optionally upload edited CIQ, and submit the selected nodes for upgrade.`;
      }
      return `${stageName}: ${capitalized(status)} Action required to be performed outside the tool and then click on 'Deployment Completed' from the actions menu.`;
    }
  } else if (pipeline?.metadata?.ProductLine?.toLowerCase().includes('rapps')) {
    if (stageName === WorkflowStage.Testing && status === PipelineStatus.Manual) {
      return `${stageName}: ${capitalized(status)} Action required to be performed outside the tool and the click on "Testing Completed" from the Actions menu.`;
    } else if (stageName === WorkflowStage.Testing && status === PipelineStatus.Warning) {
      return `${stageName}: ${capitalized(status)}. Some Test Cases failed or were skipped. Approve & Promote, Retry or Cancel.`;
    }
  } else if (pipeline?.metadata?.ProductLine?.toLowerCase().includes('baseband radio node')) {
    // Purpose-Built product line tooltip
    if (stageName === WorkflowStage.ArtifactsDeployment && status === PipelineStatus.Manual) {
      return `${stageName}: ${capitalized(status)} Action required to be performed outside the tool and then click on 'Deployment Completed' from the actions menu.`;
    } else if (stageName === WorkflowStage.Testing && status === PipelineStatus.Manual) {
      return `${stageName}: ${capitalized(status)} Action required to be performed outside the tool and then click on "Testing Completed" from the Actions menu.`;
    }
  }
  return isPending ? `${stageName}: pending` : `${stageName}: ${capitalized(status)}`;
};
